import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { Button } from '../ui/button';
import { LogOut, Menu, X } from 'lucide-react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useT();
  const navigate = useNavigate();
  const loc = useLocation();
  const isApp = loc.pathname.startsWith('/dashboard') || loc.pathname.startsWith('/upload') || loc.pathname.startsWith('/clips') || loc.pathname.startsWith('/videos') || loc.pathname.startsWith('/editor') || loc.pathname.startsWith('/admin') || loc.pathname.startsWith('/onboarding') || loc.pathname.startsWith('/publishing') || loc.pathname.startsWith('/growth') || loc.pathname.startsWith('/memory') || loc.pathname.startsWith('/social');  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const marketingLinks = (
    <>
      <Link to="/" className="hover:text-white transition" data-testid="nav-home">{t('nav.home')}</Link>
      <a href="/#features" className="hover:text-white transition" data-testid="nav-features">{t('nav.features')}</a>
      <Link to="/showcase" className="hover:text-white transition" data-testid="nav-showcase">{lang === 'fr' ? 'Vitrine' : 'Showcase'}</Link>
      <Link to="/pricing" className="hover:text-white transition" data-testid="nav-pricing">{t('nav.pricing')}</Link>
    </>
  );

  const appLinks = user && (
    <>
      <Link to="/dashboard" className="hover:text-white transition" data-testid="nav-dashboard">{t('nav.dashboard')}</Link>
      <Link to="/upload" className="hover:text-white transition" data-testid="nav-upload">{t('nav.upload')}</Link>
      <Link to="/publishing" className="hover:text-white transition" data-testid="nav-publishing">{t('nav.publishing')}</Link>
      {user.role === 'admin' && <Link to="/admin" className="hover:text-white transition" data-testid="nav-admin">{t('nav.admin')}</Link>}
    </>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="nav-logo">
          <img
            src="/logo.png"
            alt="HighlightAI logo"
            className="w-8 h-8 sm:w-9 sm:h-9 object-contain group-hover:rotate-3 transition-transform"
            data-testid="nav-logo-img"
          />
          <span className="font-heading text-lg sm:text-xl font-black tracking-tight">HighlightAI</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-300">
          {!isApp && marketingLinks}
          {appLinks}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language switcher */}
          <div className="hidden sm:flex items-center rounded-full border border-white/10 p-0.5 text-xs" data-testid="lang-switcher">
            <button onClick={() => setLang('fr')} className={`px-2.5 py-1 rounded-full font-medium transition ${lang === 'fr' ? 'bg-[#D4FF00] text-black' : 'text-zinc-400 hover:text-white'}`} data-testid="lang-fr">FR</button>
            <button onClick={() => setLang('en')} className={`px-2.5 py-1 rounded-full font-medium transition ${lang === 'en' ? 'bg-[#D4FF00] text-black' : 'text-zinc-400 hover:text-white'}`} data-testid="lang-en">EN</button>
          </div>

          {user ? (
            <>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10">
                {user.avatar ? <img src={user.avatar} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[#D4FF00] text-black text-xs flex items-center justify-center font-bold">{user.name?.[0]?.toUpperCase()}</div>}
                <span className="text-xs text-zinc-300" data-testid="nav-user-name">{user.name}</span>
                {user.plan === 'pro' && <span className="text-[10px] px-1.5 py-0.5 rounded acid-chip">PRO</span>}
              </div>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" size="sm" className="text-zinc-400 hover:text-white hidden md:inline-flex" data-testid="nav-logout" aria-label={t('nav.logout') || 'Logout'}>
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-block"><Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="nav-login">{t('nav.login')}</Button></Link>
              <Link to="/register">
                <Button className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold" data-testid="nav-signup">
                  {t('nav.signup')}
                </Button>
              </Link>
            </>
          )}

          {/* Mobile burger */}
          <button
            type="button"
            onClick={() => setMobileOpen(v => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/5 text-zinc-300 hover:text-white hover:bg-white/10 transition"
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl" data-testid="nav-mobile-panel">
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 text-base text-zinc-200">
            {!isApp && marketingLinks}
            {appLinks}
            <div className="pt-3 mt-1 border-t border-white/5 flex items-center justify-between">
              <div className="flex items-center rounded-full border border-white/10 p-0.5 text-xs">
                <button onClick={() => setLang('fr')} className={`px-2.5 py-1 rounded-full font-medium transition ${lang === 'fr' ? 'bg-[#D4FF00] text-black' : 'text-zinc-400'}`} data-testid="lang-fr-mobile">FR</button>
                <button onClick={() => setLang('en')} className={`px-2.5 py-1 rounded-full font-medium transition ${lang === 'en' ? 'bg-[#D4FF00] text-black' : 'text-zinc-400'}`} data-testid="lang-en-mobile">EN</button>
              </div>
              {user ? (
                <button onClick={() => { logout(); navigate('/'); }} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition" data-testid="nav-logout-mobile">
                  <LogOut className="w-4 h-4" /> {t('nav.logout') || 'Se déconnecter'}
                </button>
              ) : (
                <Link to="/login" className="text-sm text-zinc-300 hover:text-white" data-testid="nav-login-mobile">{t('nav.login')}</Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
